/**
 * features/auth/AuthAction.tsx
 * ------------------------------------------------------------------
 * In-app handler for Firebase Auth email action links (the custom
 * "action URL" configured in Firebase console → Authentication →
 * Templates). It catches the ?mode=…&oobCode=… link Firebase appends
 * and handles it ON lkcportal.com, so the wording is consistent
 * end-to-end — framed as ACCOUNT ACTIVATION, never "password reset"
 * (these users are signing in for the first time and have no old
 * password to "reset").
 *
 * Mechanism is still Firebase's secure set-your-own-password flow:
 *   verifyPasswordResetCode(oobCode) → email
 *   confirmPasswordReset(oobCode, newPassword)
 *   then auto sign-in and land in the portal.
 *
 * Rendered by App's Gate() BEFORE the auth check (the user isn't
 * signed in yet), mirroring the existing ?form=apply public route.
 * Reusable for future staff + parent onboarding.
 * ------------------------------------------------------------------
 */
import { useState, useEffect, useMemo, type FormEvent } from 'react';
import {
  Loader2,
  Lock,
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  ShieldCheck,
} from 'lucide-react';
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { SCHOOL } from '@/lib/school.config';
import { friendlyAuthError } from '@/lib/authErrors';
import SchoolLogo from '@/components/ui/SchoolLogo';

const MIN_LEN = 8;

type Phase = 'verifying' | 'ready' | 'submitting' | 'success' | 'invalid';

export default function AuthAction() {
  const { mode, oobCode } = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return { mode: p.get('mode') || '', oobCode: p.get('oobCode') || '' };
  }, []);

  const [phase, setPhase] = useState<Phase>('verifying');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Step 1 — validate the link and pull the account's email.
  useEffect(() => {
    let cancelled = false;
    async function verify() {
      // We only handle account activation (Firebase calls it
      // 'resetPassword'). Anything else, send the user to sign-in.
      if (mode !== 'resetPassword' || !oobCode) {
        if (!cancelled) setPhase('invalid');
        return;
      }
      try {
        const mail = await verifyPasswordResetCode(auth, oobCode);
        if (!cancelled) {
          setEmail(mail);
          setPhase('ready');
        }
      } catch {
        if (!cancelled) {
          setError(
            'This activation link is invalid or has expired. Ask your administrator to send you a fresh one.',
          );
          setPhase('invalid');
        }
      }
    }
    verify();
    return () => {
      cancelled = true;
    };
  }, [mode, oobCode]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    const pw = password;
    if (pw.length < MIN_LEN) {
      setError(`Password must be at least ${MIN_LEN} characters.`);
      return;
    }
    if (pw !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    setPhase('submitting');
    try {
      await confirmPasswordReset(auth, oobCode, pw);
      // Smooth path: sign the teacher straight in and drop them in the
      // portal. If sign-in hiccups for any reason, fall back to a
      // success screen with a link to the normal sign-in page.
      try {
        await signInWithEmailAndPassword(auth, email, pw);
        window.location.assign('/');
        return;
      } catch {
        setPhase('success');
      }
    } catch (err) {
      setError(friendlyAuthError(err, 'Could not set your password. Please try again.'));
      setPhase('ready');
    }
  }

  const busy = phase === 'submitting';

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Brand panel — matches Login */}
      <div className="lg:w-1/2 text-white flex flex-col justify-center px-10 py-16 relative overflow-hidden bg-gradient-to-br from-[#0f1b30] via-primary to-primaryDark">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-secondary/8 blur-3xl" />
        <div className="absolute -bottom-40 -left-20 w-[400px] h-[400px] rounded-full bg-primaryLight/15 blur-3xl" />
        <div className="relative z-10 max-w-md mx-auto">
          <div className="flex items-center gap-4">
            <SchoolLogo size={72} />
            <div>
              <h1 className="text-2xl font-extrabold leading-tight">{SCHOOL.name}</h1>
              <p className="text-secondary font-semibold text-sm tracking-wide">
                {SCHOOL.motto} — {SCHOOL.mottoTranslation}
              </p>
            </div>
          </div>

          <div className="mt-10 mb-2">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-sm font-medium text-white/90">
              <span className="w-2 h-2 rounded-full animate-pulse bg-secondary" />
              Account Activation
            </span>
          </div>

          <p className="mt-4 text-lg text-white/80 leading-relaxed font-medium">
            Welcome to the {SCHOOL.platformName}. Set a password to finish setting up your
            account, then sign in to see your classes and students.
          </p>

          <div className="mt-8 space-y-3">
            {[
              'Your account is ready — you just need a password',
              'You choose it yourself; no one else can see it',
              'Use it with your email to sign in from then on',
            ].map((line) => (
              <div key={line} className="flex items-start gap-2.5">
                <ShieldCheck size={16} className="text-secondary mt-0.5 shrink-0" />
                <span className="text-sm text-white/70 leading-tight">{line}</span>
              </div>
            ))}
          </div>

          <div className="mt-10 flex items-center gap-4 text-xs text-white/30">
            <span>{SCHOOL.contact.addressLines.join(' · ')}</span>
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="lg:w-1/2 flex items-center justify-center px-6 py-16 bg-white dark:bg-gray-900">
        <div className="w-full max-w-sm">
          {phase === 'verifying' && (
            <div className="flex flex-col items-center justify-center text-center py-10">
              <Loader2 size={28} className="animate-spin text-primary" />
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Checking your activation link…
              </p>
            </div>
          )}

          {phase === 'invalid' && (
            <div className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-red-50 dark:bg-red-900/30 flex items-center justify-center mb-4">
                <AlertCircle size={24} className="text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Activation link problem
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                {error || 'This link can no longer be used.'}
              </p>
              <a
                href="/"
                className="inline-block mt-6 text-sm text-primary hover:underline dark:text-primaryLight"
              >
                Go to sign in →
              </a>
            </div>
          )}

          {phase === 'success' && (
            <div className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center mb-4">
                <CheckCircle2 size={24} className="text-emerald-600 dark:text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Your account is active
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Your password has been set. You can now sign in with your email and the
                password you just chose.
              </p>
              <a
                href="/"
                className="inline-flex items-center justify-center gap-2 w-full mt-6 bg-gradient-to-r from-primary to-primaryDark hover:from-primaryDark hover:to-primary text-white font-semibold py-3 rounded-xl transition-all shadow-lg shadow-primary/20"
              >
                Go to sign in
              </a>
            </div>
          )}

          {(phase === 'ready' || phase === 'submitting') && (
            <>
              <div className="mb-8">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-medium mb-3 dark:bg-amber-900/30 dark:text-amber-300">
                  <KeyRound size={12} />
                  First-time setup
                </span>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Set your password
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Activating the account for{' '}
                  <span className="font-semibold text-gray-700 dark:text-gray-200">{email}</span>.
                  Choose a password to finish.
                </p>
              </div>

              {error && (
                <div className="mb-5 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    New password
                  </label>
                  <div className="relative">
                    <Lock
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-9 pr-10 py-2.5 rounded-xl border-2 border-gray-200 focus:border-primary focus:outline-none dark:bg-gray-800 dark:border-gray-700 dark:text-white transition-colors"
                      placeholder={`At least ${MIN_LEN} characters`}
                    />
                    <button
                      type="button"
                      aria-label="Press and hold to reveal password"
                      title="Press and hold to reveal"
                      onMouseDown={() => setShowPassword(true)}
                      onMouseUp={() => setShowPassword(false)}
                      onMouseLeave={() => setShowPassword(false)}
                      onTouchStart={() => setShowPassword(true)}
                      onTouchEnd={() => setShowPassword(false)}
                      onTouchCancel={() => setShowPassword(false)}
                      onBlur={() => setShowPassword(false)}
                      onContextMenu={(e) => e.preventDefault()}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors select-none"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Confirm password
                  </label>
                  <div className="relative">
                    <Lock
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl border-2 border-gray-200 focus:border-primary focus:outline-none dark:bg-gray-800 dark:border-gray-700 dark:text-white transition-colors"
                      placeholder="Re-enter your password"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={busy}
                  className="w-full flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-xl transition-all disabled:opacity-60 shadow-lg bg-gradient-to-r from-primary to-primaryDark hover:from-primaryDark hover:to-primary shadow-primary/20"
                >
                  {busy && <Loader2 size={16} className="animate-spin" />}
                  {busy ? 'Setting up…' : 'Activate account'}
                </button>
              </form>

              <div className="mt-10 pt-6 border-t border-gray-100 dark:border-gray-800">
                <p className="text-xs text-gray-400 text-center">
                  {SCHOOL.name} · {SCHOOL.platformName}
                </p>
                <p className="text-[10px] text-gray-300 text-center mt-1">
                  {SCHOOL.contact.phonePrimary} · {SCHOOL.contact.email}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
